import { AlertCircle, CheckCircle2, TriangleAlert, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { LAYERS, portalHost } from '../lib/floating';

/**
 * "That worked."
 *
 * ## Why this exists
 *
 * Most actions in this console changed something and said nothing. A contract
 * was sent, a token recorded, a package request answered — the list refreshed
 * underneath and the person was left to infer from a row that moved whether the
 * thing they pressed had happened. On a slow connection in Kisii, where the row
 * takes a second to arrive, "did that work?" is answered by pressing the button
 * again, which is how a contract gets sent twice.
 *
 * ## Why it portals, and why it is `role="status"`
 *
 * Portalled for the same reason every other overlay here is: `<main>` is a
 * stacking context, so a fixed child of it can be painted over by the footer —
 * that bug has already shipped once on this codebase and is not shipping again.
 *
 * `role="status"` with `aria-live="polite"` means a screen reader announces it
 * when it appears without interrupting whatever is being read. `alert` would be
 * the wrong register: a success is not an emergency.
 *
 * ## Why some of them do not disappear
 *
 * A toast with an action on it — "the contract was sent, go back to the list" —
 * waits to be dismissed. Anything that vanishes on a timer cannot be the only
 * place an instruction lives, and somebody looking away for four seconds should
 * not lose it.
 */
export type ToastKind = 'success' | 'error';

type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
  /** A toast carrying one of these stays until dismissed. */
  action?: { label: string; onClick: () => void };
};

/**
 * Something went wrong and there is something to do about it.
 *
 * ## Why this is a dialog and not a toast
 *
 * A toast is for news. This is for a question — a payment that did not go, a
 * meter that cannot be deleted, a template that will not save — where the
 * person has to understand what happened before they try again. A message in
 * the corner competes with the page it is explaining, and a message printed
 * *into* the page is worse: it appears under whatever they were looking at,
 * it is missed on a phone, and the same red paragraph can sit there for ten
 * minutes after the thing it describes stopped being true.
 *
 * A dialog stops the page, says its piece, and is dismissed deliberately. It
 * also cannot be left behind: closing it is the only way out, so nobody ends
 * up reading yesterday's failure.
 *
 * ## The three parts
 *
 * The same shape the Power screen and `PanelError` already use, because it is
 * the house standard and it is what somebody needs: what failed, the likely
 * cause worded as a guess, and one thing to do.
 */
export type Problem = {
  /** What failed, named. "The payment could not be sent." */
  what: string;
  /** Why it probably happened, hedged honestly. Optional. */
  cause?: string;
  /** The single next action. Optional where there is genuinely none. */
  fix?: string;
  /** The underlying message, for whoever wants it. */
  detail?: string;
};

type ToastApi = {
  /** Say that something worked. */
  done: (message: string, action?: Toast['action']) => void;
  /** Say that something did not. Errors never auto-dismiss. */
  failed: (message: string) => void;
  /**
   * Stop and explain. For a failure somebody has to act on.
   *
   * Given a bare `Error`, the message becomes `what` — which is the honest
   * minimum, and better than a toast that scrolls away while somebody reads
   * the form behind it.
   */
  problem: (problem: Problem | Error) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

/** Long enough to read a sentence, short enough not to sit in the way. */
const LINGER_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, action?: Toast['action']) => {
      const id = next.current;
      next.current += 1;
      setToasts((current) => [...current, { id, kind, message, action }]);
      return id;
    },
    [],
  );

  const [problem, setProblem] = useState<Problem | null>(null);

  const api = useMemo<ToastApi>(
    () => ({
      done: (message, action) => void push('success', message, action),
      failed: (message) => void push('error', message),
      problem: (input) =>
        setProblem(
          input instanceof Error
            ? /*
               * A bare error still gets the dialog. The wording is whatever
               * the API sent, which in this application is usually a full
               * sentence written for a person — see the refusals in the power
               * module — so it is worth showing as it stands rather than
               * wrapping in "An error occurred".
               */
              { what: input.message }
            : input,
        ),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-0 flex flex-col items-center gap-2 p-4 print:hidden"
          style={{ zIndex: LAYERS.dialog }}
        >
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDone={() => remove(toast.id)} />
          ))}
        </div>,
        portalHost(),
      )}

      {problem && <ProblemDialog problem={problem} onClose={() => setProblem(null)} />}
    </ToastContext.Provider>
  );
}

/**
 * The popup itself.
 *
 * Deliberately plain: a heading, up to three short paragraphs, and one way
 * out. Anything more and it becomes a page of its own, which is what it is
 * replacing.
 */
function ProblemDialog({ problem, onClose }: { problem: Problem; onClose: () => void }) {
  /*
   * Escape closes it, because a dialog that traps somebody is worse than the
   * banner it replaced. The button is still the obvious way out.
   */
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-navy-900/40 p-4 print:hidden"
      style={{ zIndex: LAYERS.dialog }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="problem-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-card"
        /* The backdrop closes it; a press inside must not. */
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-danger-500" aria-hidden="true" />
          <div className="min-w-0">
            <h2 id="problem-title" className="font-heading text-lg">
              {problem.what}
            </h2>

            {problem.cause && <p className="mt-2 text-sm">{problem.cause}</p>}
            {problem.fix && <p className="mt-2 text-sm font-medium">{problem.fix}</p>}

            {/*
              The raw message, kept and kept quiet. Whoever is debugging wants
              the exact words; everybody else is helped by the lines above and
              confused by provider jargon.
            */}
            {problem.detail && (
              <p className="mt-3 break-words text-xs text-muted-foreground">{problem.detail}</p>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button type="button" className="btn-primary" autoFocus onClick={onClose}>
            I see
          </button>
        </div>
      </div>
    </div>,
    portalHost(),
  );
}

function ToastCard({ toast, onDone }: { toast: Toast; onDone: () => void }) {
  /*
   * Errors and anything with an action wait to be dismissed. A message telling
   * somebody what to do next is not a message to take away after four seconds.
   */
  const sticky = toast.kind === 'error' || Boolean(toast.action);

  useEffect(() => {
    if (sticky) return;
    const timer = window.setTimeout(onDone, LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [sticky, onDone]);

  const success = toast.kind === 'success';

  return (
    <div
      className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border p-3 shadow-card ${
        success
          ? 'border-secondary-300 bg-secondary-50 text-secondary-900'
          : 'border-danger-300 bg-danger-50 text-danger-800'
      }`}
    >
      {success ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary-600" aria-hidden="true" />
      ) : (
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger-600" aria-hidden="true" />
      )}

      <div className="min-w-0 flex-1">
        <p className="text-sm">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            className="btn-secondary mt-2 text-sm"
            onClick={() => {
              toast.action?.onClick();
              onDone();
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onDone}
        aria-label="Dismiss"
        className="shrink-0 rounded-lg p-1 hover:bg-black/5"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Say that something worked, from anywhere.
 *
 * Returns a no-op outside the provider rather than throwing. A missing toast is
 * a missing confirmation; it is not a reason to take a screen down, and the
 * signing page renders outside the console shell entirely.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  return (
    api ?? {
      done: () => undefined,
      failed: () => undefined,
      /*
       * Outside the console shell — the signing page, the tracking page — a
       * failure still has to be seen, and there is no provider to show it. The
       * browser's own dialog is ugly and unmissable, which is the right trade
       * on a page somebody reached from a text message.
       */
      problem: (input) =>
        window.alert(input instanceof Error ? input.message : [input.what, input.cause, input.fix].filter(Boolean).join('\n\n')),
    }
  );
}
